import { CircleHelp } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../ui/accordion";
import { useState } from "react";

// Define types for the props
interface MenuItem {
  title: string;
  icon: React.ElementType;
  links?: { title: string; href: string }[];
  subMenuItems?: MenuItem[];
}

interface VerticalMenuProps {
  menuTitle?: string;
  menuIcon?: React.ElementType;
  items?: MenuItem[];
  setActiveCategory: React.Dispatch<React.SetStateAction<string>>;
  setSubActiveCategory: React.Dispatch<React.SetStateAction<string>>;
  subActiveCategory: string;
}

export function VerticalMenu({
  menuTitle = "Menu",
  menuIcon: MenuIcon = CircleHelp,
  items = [],
  setActiveCategory,
  setSubActiveCategory,
  subActiveCategory, // Added prop
}: VerticalMenuProps) {
  const [activeItem, setActiveItem] = useState<string>(
    items.length ? items[0].title.toLowerCase() : ""
  );
  const [openAccordion, setOpenAccordion] = useState<string | undefined>();

  const handleItemClick = (title: string) => {
    setActiveItem(title.toLowerCase());
    setActiveCategory(title);
    setSubActiveCategory("");
  };

  return (
    <div className="w-full max-w-sm border rounded-lg md:max-w-none md:w-72">
      <div className="flex items-center gap-2 p-3 text-sm font-medium border-b">
        {MenuIcon && <MenuIcon className="w-4 h-4" />}
        <span className="truncate">{menuTitle}</span>
      </div>

      <Accordion
        type="single"
        value={openAccordion}
        onValueChange={setOpenAccordion}
      >
        {items.map((item, index) =>
          item.links?.length || item.subMenuItems?.length ? (
            <AccordionItem key={index} value={item.title.toLowerCase()}>
              <AccordionTrigger
                onClick={() => {
                  handleItemClick(item.title);
                  setOpenAccordion(item.title.toLowerCase());
                }}
                className={`px-3 py-2 text-sm font-medium hover:no-underline ${
                  activeItem === item.title.toLowerCase()
                    ? "bg-primary text-white"
                    : ""
                }`}
              >
                <div className="flex items-center min-w-0 gap-2">
                  <item.icon className="flex-shrink-0 w-4 h-4" />
                  <span className="truncate">{item.title.toUpperCase()}</span>
                </div>
              </AccordionTrigger>
              <AccordionContent>
                <div className="flex flex-col text-sm">
                  {item.links?.map((link, idx) => (
                    <div
                      key={idx}
                      className={`px-6 sm:px-8 py-2 hover:bg-gray-50 cursor-pointer transition-colors ${
                        subActiveCategory === link.title
                          ? "font-bold text-primary"
                          : ""
                      }`}
                      onClick={() => setSubActiveCategory(link.title)}
                    >
                      <span className="block truncate">{link.title}</span>
                    </div>
                  ))}
                </div>
              </AccordionContent>
            </AccordionItem>
          ) : (
            <div
              key={index}
              className={`px-3 py-2 text-sm font-medium flex items-center gap-2 cursor-pointer hover:bg-gray-50 transition-colors min-w-0 ${
                activeItem === item.title.toLowerCase()
                  ? "bg-primary text-white"
                  : ""
              }`}
              onClick={() => handleItemClick(item.title)}
            >
              <item.icon className="flex-shrink-0 w-4 h-4" />
              <span className="truncate">{item.title.toUpperCase()}</span>
            </div>
          )
        )}
      </Accordion>
    </div>
  );
}
